import React, { useCallback } from 'react'

import { arrayOf, bool, func, number, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'
import SharedOrderSummary from 'Pods/Shared/OrderSummary/OrderSummary'

import formatPrice from 'Utils/formatPrice'
import { NOOP } from 'Utils/stableDefaults'

const TRANSLATION_KEY_PREFIX = 'extra_usage_checkout.order_summary'

function OrderSummary({
  locale = 'en',
  itemTitle,
  itemBullets,
  itemPrice,
  subtotal,
  vatAmount = 0,
  total,
  onPay: handlePay = NOOP,
  disabled = true,
}) {
  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale],
  )

  const itemCard = (
    <div className="order-summary__item">
      <div className="order-summary__item-content">
        <p className="order-summary__item-title cgt-body-base-semibold">{itemTitle}</p>
        <ul className="order-summary__bullets">
          {itemBullets.map((bullet) => (
            <li key={bullet} className="order-summary__bullet cgt-caption">
              <span className="order-summary__bullet-dot" />
              {bullet}
            </li>
          ))}
        </ul>
      </div>
      <p className="order-summary__item-price cgt-body-lg-semibold">{formatPrice(itemPrice)}</p>
    </div>
  )

  const legal = (
    <>
      {translate(`${TRANSLATION_KEY_PREFIX}.legal_prefix`)}{' '}
      <a
        className="cgt-link-primary-accented"
        href="https://www.cgtrader.com/pages/terms-and-conditions"
        target="_blank"
        rel="noreferrer"
      >
        {translate(`${TRANSLATION_KEY_PREFIX}.legal_terms`)}
      </a>
      {' '}{translate(`${TRANSLATION_KEY_PREFIX}.legal_connector`)}{' '}
      <a
        className="cgt-link-primary-accented"
        href="https://www.cgtrader.com/pages/privacy-policy"
        target="_blank"
        rel="noreferrer"
      >
        {translate(`${TRANSLATION_KEY_PREFIX}.legal_privacy`)}
      </a>
      .
    </>
  )

  return (
    <SharedOrderSummary
      title={translate(`${TRANSLATION_KEY_PREFIX}.title`)}
      subtotal={subtotal}
      subtotalLabel={translate(`${TRANSLATION_KEY_PREFIX}.subtotal`)}
      vatAmount={vatAmount}
      vatLabel={translate(`${TRANSLATION_KEY_PREFIX}.vat_label`)}
      vatTooltip={translate(`${TRANSLATION_KEY_PREFIX}.vat_tooltip`)}
      total={total}
      totalLabel={translate(`${TRANSLATION_KEY_PREFIX}.order_total`)}
      payLabel={translate(`${TRANSLATION_KEY_PREFIX}.pay_button`, { amount: formatPrice(total) })}
      onPay={handlePay}
      disabled={disabled}
      legal={legal}
    >
      {itemCard}
    </SharedOrderSummary>
  )
}

OrderSummary.propTypes = {
  locale: string,
  itemTitle: string.isRequired,
  itemBullets: arrayOf(string).isRequired,
  itemPrice: number.isRequired,
  subtotal: number.isRequired,
  vatAmount: number,
  total: number.isRequired,
  onPay: func,
  disabled: bool,
}

export default OrderSummary
